// Google Maps JS API loader + services helper.
// Reads API key from Site Config (admin editable) with env fallback.
// Distance results are cached in-memory to reduce API calls.
// Failures handled:
//   1. Auth-level (referrer/invalid key)  → gm_authFailure global
//   2. Service-level (billing/quota/API not enabled) → detected via Distance
//      Matrix REQUEST_DENIED/OVER_QUERY_LIMIT/UNKNOWN_ERROR status. Once flagged,
//      the UI stops calling Google services and falls back to bundled distances.

let scriptPromise = null;
let cachedKey = null;
let mapsGlobal = null;
let authError = false;
let serviceError = false;
let serviceErrorReason = "";
let keyListeners = [];

const distanceCache = new Map();

const notify = () => keyListeners.forEach((fn) => { try { fn(); } catch (_) {} });

export function subscribeGoogleMapsKey(cb) {
    keyListeners.push(cb);
    return () => {
        keyListeners = keyListeners.filter((fn) => fn !== cb);
    };
}

export function setGoogleMapsApiKey(key) {
    if (!key || typeof key !== "string" || !key.trim()) return;
    const trimmed = key.trim();
    if (cachedKey === trimmed) return;
    cachedKey = trimmed;
    if (!mapsGlobal) {
        scriptPromise = null;
        authError = false;
        serviceError = false;
        serviceErrorReason = "";
    }
    notify();
}

export function getGoogleMapsApiKey() {
    return cachedKey || process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
}

export const isGoogleMapsConfigured = () =>
    !!getGoogleMapsApiKey() && !authError && !serviceError;

export const hasGoogleMapsAuthError = () => authError;
export const hasGoogleMapsServiceError = () => serviceError;
export const getGoogleMapsErrorReason = () => {
    if (authError) return "auth";
    if (serviceError) return serviceErrorReason || "service";
    return "";
};

function flagServiceError(reason) {
    if (serviceError) return;
    serviceError = true;
    serviceErrorReason = reason || "service";
    // eslint-disable-next-line no-console
    console.warn(
        `[GoogleMaps] Service unavailable: ${reason}. Falling back to bundled distances.`,
    );
    notify();
}

if (typeof window !== "undefined" && !window.__vvpss_gm_authfail_hooked) {
    window.__vvpss_gm_authfail_hooked = true;
    window.gm_authFailure = () => {
        authError = true;
        scriptPromise = null;
        mapsGlobal = null;
        // eslint-disable-next-line no-console
        console.warn(
            "[GoogleMaps] Auth failure. Check API key validity, HTTP referrer restrictions, or that Maps JS + Places + Distance Matrix APIs are enabled.",
        );
        notify();
    };
}

function bootstrapGoogleMaps() {
    if (typeof window === "undefined") return Promise.resolve(null);
    if (authError || serviceError) {
        console.warn(
            `[GoogleMaps] loadGoogleMaps early-return — auth=${authError} service=${serviceError} reason=${serviceErrorReason}`,
        );
        return Promise.resolve(null);
    }
    const key = getGoogleMapsApiKey();
    if (!key) {
        console.warn("[GoogleMaps] No API key configured");
        return Promise.resolve(null);
    }
    if (mapsGlobal) return Promise.resolve(mapsGlobal);
    if (scriptPromise) return scriptPromise;

    scriptPromise = new Promise((resolve, reject) => {
        const importAll = async () => {
            try {
                const g = window.google;
                mapsGlobal = g;
                resolve(mapsGlobal);
            } catch (e) {
                console.error("[GoogleMaps] importLibrary failed:", e);
                resolve(null);
            }
        };

        // After script.onload the bootloader may still be installing
        // `google.maps.importLibrary`. Poll for it (up to 5 s) before
        // calling importLibrary — this eliminates a subtle race we
        // observed where onload fires before importLibrary is defined.
        const finalise = () => {
            const start = Date.now();
            const tick = () => {
                if (authError) {
                    console.error("[GoogleMaps] authError flagged during bootloader wait");
                    return resolve(null);
                }
                const g = window.google;
                if (g && g.maps && typeof g.maps.importLibrary === "function") {
                    return importAll();
                }
                if (Date.now() - start > 5000) {
                    console.error(
                        "[GoogleMaps] Timed out waiting for google.maps.importLibrary. " +
                        "Check API key + referrer + enabled APIs (Maps JS / Places / Routes / Geocoding / Geometry).",
                    );
                    return resolve(null);
                }
                setTimeout(tick, 50);
            };
            tick();
        };

        const existing = document.querySelector('script[data-google-maps="true"]');
        if (existing) {
            if (window.google && window.google.maps && typeof window.google.maps.importLibrary === "function") {
                importAll();
            } else {
                existing.addEventListener("load", finalise);
                existing.addEventListener("error", (e) => {
                    console.error("[GoogleMaps] Existing script tag failed to load:", e);
                    reject(new Error("Google Maps failed to load"));
                });
                // Kick off polling immediately in case the script already
                // fired 'load' before we attached the listener.
                finalise();
            }
            return;
        }
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
            key,
        )}&loading=async&v=weekly`;
        script.async = true;
        script.defer = true;
        script.dataset.googleMaps = "true";
        script.onload = finalise;
        script.onerror = (e) => {
            scriptPromise = null;
            console.error("[GoogleMaps] Script tag onerror:", e);
            reject(new Error("Google Maps failed to load"));
        };
        document.head.appendChild(script);
    });
    return scriptPromise;
}

const libraryPromises = new Map();
export async function loadGoogleMaps(libraries = ["maps"]) {
    const google = await bootstrapGoogleMaps();
    if (!google) return null;
    await Promise.all(libraries.map((name) => {
        if (!libraryPromises.has(name)) {
            libraryPromises.set(name, google.maps.importLibrary(name).catch((error) => {
                libraryPromises.delete(name);
                throw error;
            }));
        }
        return libraryPromises.get(name);
    }));
    return google;
}

// Distance Matrix via JS SDK, with in-memory cache and service-error detection.
// Returns null (rather than throwing) for hard failures so callers can fall back
// to the bundled route table without polluting the promise chain.
export function getDrivingDistance(origin, destination) {
    const key = `${(origin || "").trim().toLowerCase()}|${(destination || "").trim().toLowerCase()}`;
    if (distanceCache.has(key)) return Promise.resolve(distanceCache.get(key));
    if (serviceError || authError) return Promise.resolve(null);

    return loadGoogleMaps(["routes", "core"]).then((google) => {
        if (!google) return null;
        return new Promise((resolve) => {
            const service = new google.maps.DistanceMatrixService();
            service.getDistanceMatrix(
                {
                    origins: [origin],
                    destinations: [destination],
                    travelMode: google.maps.TravelMode.DRIVING,
                    unitSystem: google.maps.UnitSystem.METRIC,
                    avoidHighways: false,
                    avoidTolls: false,
                },
                (response, status) => {
                    // Hard, non-recoverable failures — flag service unavailable for the session.
                    if (
                        status === "REQUEST_DENIED" ||
                        status === "OVER_QUERY_LIMIT" ||
                        status === "UNKNOWN_ERROR" ||
                        status === "INVALID_REQUEST"
                    ) {
                        flagServiceError(status);
                        return resolve(null);
                    }
                    if (status !== "OK" || !response) return resolve(null);
                    const row = response.rows?.[0]?.elements?.[0];
                    if (!row || row.status !== "OK") return resolve(null);
                    const result = {
                        distanceMeters: row.distance.value,
                        distanceKm: Math.round(row.distance.value / 1000),
                        distanceText: row.distance.text,
                        durationSeconds: row.duration.value,
                        durationText: row.duration.text,
                    };
                    distanceCache.set(key, result);
                    resolve(result);
                },
            );
        });
    });
}
